import { ClipboardText } from 'shared/icons';
import { useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import CountOrderCreateModal from '../../components/warehouse/inventory/CountOrderCreateModal';
import InventoryAgingPanel from '../../components/warehouse/inventory/InventoryAgingPanel';
import InventoryOccupancyPanel from '../../components/warehouse/inventory/InventoryOccupancyPanel';
import InventoryReconciliationPanel from '../../components/warehouse/inventory/InventoryReconciliationPanel';
import InventorySlaPanel from '../../components/warehouse/inventory/InventorySlaPanel';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import Button from '../../components/ui/Button';
import { useAuth } from '../../hooks/useAuth';

/**
 * /warehouse/inventory — CONSOLE DE INVENTÁRIO (M9, Supervisor, desktop).
 *
 * Quatro perguntas, quatro abas, uma origem: **o galpão que já está gravado**.
 * Nenhum número desta página vem de contador — ocupação sai de `wms_locations`
 * × `wms_lpns`, idade sai de `wms_lpns.created_at`, SLA sai dos carimbos da
 * própria OS, e a conciliação soma a trilha. É a regra que manda no módulo:
 * painel que mostra número que a trilha não sustenta é pior que painel nenhum.
 *
 * A aba CONCILIAÇÃO existe justamente para essa regra não ser uma promessa: ela
 * é o DoD ("os números reconciliam com `wms_stock_movements`") em forma de tela,
 * verificável a qualquer momento por quem abrir.
 *
 * ⚠️ A CONTAGEM não tem aba própria: ela é uma OS, mora no Kanban de OSs (M5) e
 * é executada no coletor. O que existe aqui é o BOTÃO de criar — porque o
 * critério de escolha dos blocos é o heatmap desta página.
 */

const TABS = [
    { id: 'occupancy', labelKey: 'wms.tab.occupancy' },
    { id: 'aging', labelKey: 'wms.tab.aging' },
    { id: 'sla', labelKey: 'wms.tab.sla' },
    { id: 'reconciliation', labelKey: 'wms.tab.reconciliation' },
];

const WarehouseInventoryPage = () => {
    const { label } = useWarehouseLabels();
    const { checkPermission } = useAuth();
    const [activeTab, setActiveTab] = useState('occupancy');
    const [countOpen, setCountOpen] = useState(false);
    const [createdOrder, setCreatedOrder] = useState(null);

    // Criar a OS de contagem é ato do console de OSs (M5) — e é a permissão DELE
    // que o backend consulta, porque a contagem nasce pelo `POST /service-orders`
    // que já existe. Ler o painel é `wms:manage:inventory`, que a rota desta
    // página já exige; quem só audita não cria ordem de serviço.
    const canCreateCount = checkPermission('wms:manage:service_orders');

    const tabs = useMemo(() => TABS.map((tab) => ({ ...tab, label: label(tab.labelKey) })), [label]);

    return (
        <div className="space-y-6">
            <PageHeader
                title={label('wms.tab.inventory')}
                subtitle={label('wms.reconciliation.hint')}
                actions={
                    canCreateCount ? (
                        <Button onClick={() => setCountOpen(true)}>
                            <ClipboardText size={18} className="mr-2" />
                            {label('wms.count.new')}
                        </Button>
                    ) : null
                }
            />

            {/* RECIBO da contagem criada — os códigos gerados ficam na tela
                (mesma disciplina das levas de alocação do M5: aviso passageiro
                some antes de o Supervisor conseguir falar "OS zero-zero-um"). */}
            {createdOrder ? (
                <div className="flex items-start justify-between gap-3 rounded-xl border-2 border-success-400 bg-success-soft px-4 py-3 dark:border-success-700 dark:bg-success-900/25">
                    <div>
                        <p className="text-sm font-bold text-success-text dark:text-success-200">
                            {label('wms.count.new')}: <span className="font-mono">{createdOrder.code}</span>
                        </p>
                        <p className="text-sm text-success-text dark:text-success-200">
                            {createdOrder.items_count} {label('wms.count.step_location').toLowerCase()} ·{' '}
                            {label('wms.count.blocked_excluded')}
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setCreatedOrder(null)}>
                        {label('wms.common.close')}
                    </Button>
                </div>
            ) : null}

            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {tabs.map((tab) => {
                        const isActive = tab.id === activeTab;
                        return (
                            <button
                                type="button"
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'border-black text-black dark:border-white dark:text-white'
                                        : 'border-transparent text-mono-500 hover:border-mono-300 hover:text-mono-700 dark:hover:text-mono-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {activeTab === 'occupancy' ? <InventoryOccupancyPanel label={label} /> : null}
            {activeTab === 'aging' ? <InventoryAgingPanel label={label} /> : null}
            {activeTab === 'sla' ? <InventorySlaPanel label={label} /> : null}
            {activeTab === 'reconciliation' ? <InventoryReconciliationPanel label={label} /> : null}

            <CountOrderCreateModal
                open={countOpen}
                label={label}
                onClose={() => setCountOpen(false)}
                onCreated={(order) => {
                    setCountOpen(false);
                    setCreatedOrder(order);
                }}
            />
        </div>
    );
};

export default WarehouseInventoryPage;
